import clsx from 'clsx';
import React, { useMemo, useState } from 'react';
import { QUERY_DEBOUNCE_TIME } from '../../../core/constants/constants';
import { matchPattern } from '../../../core/utils/string';
import { Target } from '../models/target';
import { TargetObjectMeta } from '../models/target-object-meta';
import SearchArea from '../../../list/components/search-area/SearchArea';
import { ObjectExplorerBodyShimmer } from './ObjectExplorerBodyShimmer';
import { ObjectExplorerFields } from './ObjectExplorerFields';
import { ObjectExplorerFieldsEmpty } from './ObjectExplorerFieldsEmpty';
import { ObjectExplorerFeildsFetchError } from './ObjectExplorerFieldsFetchError';
import { ObjectExplorerHead } from './ObjectExplorerHead';
import { ObjectExplorerHeadShimmer } from './ObjectExplorerHeadShimmer';
import { ObjectExplorerTargetReauth } from './ObjectExplorerTargetReauth';
import { ObjectExplorerUnselectedTarget } from './ObjectExplorerUnselectedTarget';
import styles from './styles.module.scss';
import { useObjectExplorer } from './useObjectExplorer';

export interface TargetObjectExplorerProps {
  className?: string;
  target: Target;
  currentTargetObject: TargetObjectMeta;
  targetObjectChanged: (targetObject: TargetObjectMeta) => any;
}

export function TargetObjectExplorer({
  className = '',
  target,
  currentTargetObject,
  targetObjectChanged
}: TargetObjectExplorerProps) {
  const [ searchTerm, setSearchTerm ] = useState('');

  const {
    objects,
    loadingObjects,
    selectedTargetObject,
    selectTargetObject,
    needTargetReauth,
    fields,
    loadingFields,
    fetchingFieldsError,
    lastRefreshedTs
  } = useObjectExplorer({
    target,
    currentTargetObject,
    targetObjectChanged
  });

  const filteredFields = useMemo(() => {
    return fields.filter((field) => {
      return searchTerm
        ? matchPattern([ field.name, field.type ], searchTerm)
        : true;
    });
  }, [ searchTerm, fields ]);

  const onSchemaSearch = (value: string) => {
    setSearchTerm(value);
  };

  return (
    <div className={clsx(styles.objectExplorerWrapper, 'surface-box', className)}>
      {!loadingObjects ? (
        <ObjectExplorerHead
          target={target}
          objects={objects}
          selectedTargetObject={selectedTargetObject}
          selectTargetObject={selectTargetObject}
          lastRefreshedTs={lastRefreshedTs}
          needTargetReauth={needTargetReauth}
          loadingFields={loadingFields}
        />
      ) : (
        <ObjectExplorerHeadShimmer />
      )}

      {!needTargetReauth && !loadingFields && selectedTargetObject ? (<>
        {!fetchingFieldsError ? (<>
          <div className={styles.objectExplorerSearch}>
            <SearchArea
              dataId='target-object-explorer'
              fullWidth
              collapsible={false}
              hasBorder={false}
              iconSize={2}
              onSearch={onSchemaSearch}
              debounceInterval={QUERY_DEBOUNCE_TIME}
              placeholder='Search Target Fields'
            />
          </div>

          {filteredFields?.length ? (
            <ObjectExplorerFields fields={filteredFields} />
          ) : (
            <div className={styles.objectExplorerEmpty}>
              <ObjectExplorerFieldsEmpty />
            </div>
          )}
        </>) : (
          <div className={styles.objectExplorerEmpty}>
            <ObjectExplorerFeildsFetchError error={fetchingFieldsError} />
          </div>
        )}
      </>) : null}

      {!needTargetReauth && !loadingObjects && !selectedTargetObject ? (
        <div className={styles.objectExplorerEmpty}>
          <ObjectExplorerUnselectedTarget />
        </div>
      ) : null}

      {loadingObjects || loadingFields ? (
        <ObjectExplorerBodyShimmer />
      ) : null}

      {needTargetReauth ? (<>
        <div className={styles.objectExplorerEmpty}>
          <ObjectExplorerTargetReauth target={target} />
        </div>
      </>) : null}
    </div>
  );
}
